import { Alert, Button, Drawer, SvgIcon } from '@salecycle/ui-component-library';
import { useTranslation } from 'react-i18next';
import { DrawVariantCard } from './components/DrawVariantCard';
import styled, { useTheme } from 'styled-components';
import { useVariantsSelector } from '../../../state/SplitTests/selectors/useVariantsSelector';
import { InfoTooltip } from 'sections/onsite-campaigns/pages/CampaignEditor/components/InfoTooltip';
import ControlGroup from '../DisplayWebContent/components/ControlGroup';
import { useIsSplitTestControlGroupSelector } from '../../../state/SplitTests/selectors/useIsSplitTestControlGroupSelector';
import { SchedulingControl } from './components/SchedulingControl';
import { SplitTestName } from './components/SplitTestName';
import { useState, useCallback, useEffect } from 'react';
import { useSetVariantWeight } from '../../../state/SplitTests/actions/useSetVariantWeight';

const Title = styled.div`
  font: ${({ theme }) => theme.designToken.headingH3};
`;

const FooterContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  span {
    margin-left: ${({ theme }) => theme.designToken.spacing2};
  }
`;

const DrawerContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.designToken.spacing4};
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.designToken.spacing1};
`;

const TooltipWrapper = styled.div`
  position: relative;
  top: -2px;
`;

export interface DisplayWebContentDrawerProps {
  open: boolean;
  closeDrawer: () => void;
  messageId: string;
}

export function SplitTestSettingsDrawer({ messageId, open, closeDrawer }: DisplayWebContentDrawerProps) {
  const variants = useVariantsSelector(messageId);
  const isControlGroup = useIsSplitTestControlGroupSelector(messageId);
  const theme = useTheme();
  const { t } = useTranslation('campaigns');

  const setVariantWeight = useSetVariantWeight();

  const [weights, setWeights] = useState<Record<string, number>>({});

  useEffect(() => {
    if (variants?.length) {
      const newWeights = variants.reduce((acc, variant) => {
        acc[variant.messageId] = variant.weight || 0;
        return acc;
      }, {} as Record<string, number>);

      setWeights(newWeights);
    }
  }, [variants]);

  const handleWeightChange = useCallback(
    (newWeight: number, variantId: string) => {
      const otherVariantId = variants?.find(v => v.messageId !== variantId)?.messageId;
      if (otherVariantId) {
        const remainingWeight = 100 - newWeight;
        setWeights(prevWeights => ({
          ...prevWeights,
          [variantId]: newWeight,
          [otherVariantId]: remainingWeight,
        }));

        setVariantWeight({
          id: messageId,
          variantWeights: [
            {
              messageId: variantId,
              weight: newWeight,
            },
            {
              messageId: otherVariantId,
              weight: remainingWeight,
            },
          ],
        });
      }
    },
    [messageId, variants, setVariantWeight],
  );

  return (
    <Drawer
      header={'Split test settings'}
      footer={
        <Button
          variation="secondary"
          onClick={closeDrawer}
          fullWidth
          ariaLabel={t('onsite.editor.workflow.displayWebContentMessage.drawer.closeButtonAria')}
        >
          <FooterContainer>
            <SvgIcon name="check" size="24" color={theme.designToken.primary500} />
            <span>{t('onsite.editor.workflow.drawerCloseButtonLabel')}</span>
          </FooterContainer>
        </Button>
      }
      onClose={closeDrawer}
      open={open}
    >
      <DrawerContent>
        <SplitTestName id={messageId} key={messageId} />

        <div>
          <ControlGroup messageId={messageId} key={messageId} isSplitTest={true} />
        </div>

        <SchedulingControl splitTestId={messageId} />

        <div style={{ display: 'flex', flexDirection: 'column', gap: theme.designToken.spacing4 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: theme.designToken.spacing2 }}>
            <TitleContainer>
              <Title>{t('onsite.editor.workflow.splitTest.drawer.title')}</Title>
              <TooltipWrapper>
                <InfoTooltip content={t('onsite.editor.workflow.splitTest.drawer.workFlowInfoForSplitTestTooltip')} />
              </TooltipWrapper>
            </TitleContainer>
            {isControlGroup && (
              <Alert
                aria-label={t('onsite.editor.workflow.splitTest.drawer.alertAriaLabel')}
                heading={
                  <div>
                    {t('onsite.editor.workflow.splitTest.drawer.controlGroupAlert')}
                    <span style={{ marginLeft: theme.designToken.spacing1 }}></span>
                    <InfoTooltip displayInline content={t('onsite.editor.workflow.splitTest.drawer.tooltipContent')} />
                  </div>
                }
                variation="info"
              />
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: theme.designToken.spacing3 }}>
            {variants?.map(variant => (
              <DrawVariantCard
                key={variant.messageId}
                variant={variant}
                weight={weights[variant.messageId]}
                onWeightChange={(newWeight: number) => handleWeightChange(newWeight, variant.messageId)}
              />
            ))}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
